import { c as _c } from "react/compiler-runtime";
import Translate from "@mui/icons-material/Translate";
import { alpha, Box, Divider, FormControl, InputLabel, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader, MenuItem, Select, type SelectChangeEvent, SwipeableDrawer, Typography, useTheme } from "@mui/material";
import { signOut, useSession } from "next-auth/react";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import { useOptionalCookieConsentContext } from "#/features/cookieConsent/context/CookieConsentContext";
import type { Locales } from "#/i18n/i18n-types";
import { loadLocaleAsync } from "#/i18n/i18n-util.async";
import { localeLabels, localesForLanguagePicker } from "#/i18n/labels";
import { setStoredLocale } from "#/shared/browser-storage/localeStorage";
import { useI18nContext } from "#/shared/hooks";
import { GITHUB_URL } from "#/constants";
type NavItemProps = {
  title: string;
  onClick?: () => void;
  href?: string;
};
const NavItem: React.FC<NavItemProps> = t0 => {
  const $ = _c(7);
  const {
    title,
    onClick,
    href
  } = t0;
  const component = href ? "a" : "button";
  let t1;
  if ($[0] !== title) {
    t1 = <ListItemText primary={title} />;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== component || $[3] !== href || $[4] !== onClick || $[5] !== t1) {
    t2 = <ListItem disablePadding={true}><ListItemButton component={component} href={href} onClick={onClick}>{t1}</ListItemButton></ListItem>;
    $[2] = component;
    $[3] = href;
    $[4] = onClick;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
type SidePanelProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};
export const SidePanel: React.FC<SidePanelProps> = t0 => {
  const $ = _c(83);
  const {
    isOpen,
    setIsOpen
  } = t0;
  const router = useRouter();
  const {
    LL
  } = useI18nContext();
  const theme = useTheme();
  const session = useSession();
  const cookieConsent = useOptionalCookieConsentContext();
  let t1;
  if ($[0] !== cookieConsent || $[1] !== setIsOpen) {
    t1 = () => {
      cookieConsent?.openCookieSettings();
      setIsOpen(false);
    };
    $[0] = cookieConsent;
    $[1] = setIsOpen;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleOpenCookieSettings = t1;
  let t2;
  if ($[3] !== router) {
    t2 = async event => {
      const newLocale = event.target.value as Locales;
      if (newLocale !== router.locale) {
        setStoredLocale(newLocale);
        await loadLocaleAsync(newLocale);
        router.push(router.asPath, undefined, {
          locale: newLocale
        });
      }
    };
    $[3] = router;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleChangeLocale = t2;
  let t3;
  let t4;
  if ($[5] !== setIsOpen) {
    t3 = () => setIsOpen(true);
    t4 = () => setIsOpen(false);
    $[5] = setIsOpen;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  let t5;
  if ($[8] !== theme.appDesign.surfaceHigh) {
    t5 = alpha(theme.appDesign.surfaceHigh, 0.88);
    $[8] = theme.appDesign.surfaceHigh;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== theme.appDesign.outline) {
    t6 = alpha(theme.appDesign.outline, 0.16);
    $[10] = theme.appDesign.outline;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = `1px solid ${t6}`;
  let t8;
  if ($[12] !== t5 || $[13] !== t7) {
    t8 = {
      paper: {
        sx: {
          background: t5,
          backdropFilter: "blur(20px)",
          borderLeft: t7
        }
      }
    };
    $[12] = t5;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      minWidth: "22vw"
    };
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== LL) {
    t10 = LL.MAIN_MENU();
    $[16] = LL;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10) {
    t11 = <ListSubheader disableSticky={true}>{t10}</ListSubheader>;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = `/profile/${session.data?.user.id ?? ""}`;
  let t13;
  if ($[20] !== LL) {
    t13 = LL.PROFILE();
    $[20] = LL;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13) {
    t14 = <NavItem title={t13} />;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t12 || $[25] !== t14) {
    t15 = <Link href={t12}>{t14}</Link>;
    $[24] = t12;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <NavItem title="GitHub" href={GITHUB_URL} />;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== LL) {
    t17 = LL.FEEDBACK();
    $[28] = LL;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t17) {
    t18 = <NavItem title={t17} href={`${GITHUB_URL}/issues`} />;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== LL) {
    t19 = LL.LOGOUT();
    $[32] = LL;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t19) {
    t20 = <NavItem title={t19} onClick={_temp} />;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t11 || $[37] !== t15 || $[38] !== t18 || $[39] !== t20) {
    t21 = <List subheader={t11}>{t15}{t16}{t18}{t20}</List>;
    $[36] = t11;
    $[37] = t15;
    $[38] = t18;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Divider />;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== LL) {
    t23 = LL.SETTINGS();
    $[42] = LL;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== t23) {
    t24 = <ListSubheader disableSticky={true}>{t23}</ListSubheader>;
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <ListItemIcon><Translate /></ListItemIcon>;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== LL) {
    t26 = LL.LANGUAGE();
    $[47] = LL;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== t26) {
    t27 = <InputLabel id="side-panel-language-label">{t26}</InputLabel>;
    $[49] = t26;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] !== LL) {
    t28 = LL.LANGUAGE();
    $[51] = LL;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  const t29 = router.locale as Locales ?? "en";
  let t30;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = localesForLanguagePicker.map(_temp2);
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  let t31;
  if ($[54] !== handleChangeLocale || $[55] !== t28 || $[56] !== t29) {
    t31 = <Select labelId="side-panel-language-label" label={t28} value={t29} onChange={handleChangeLocale}>{t30}</Select>;
    $[54] = handleChangeLocale;
    $[55] = t28;
    $[56] = t29;
    $[57] = t31;
  } else {
    t31 = $[57];
  }
  let t32;
  if ($[58] !== t27 || $[59] !== t31) {
    t32 = <ListItem>{t25}<FormControl fullWidth={true}>{t27}{t31}</FormControl></ListItem>;
    $[58] = t27;
    $[59] = t31;
    $[60] = t32;
  } else {
    t32 = $[60];
  }
  let t33;
  if ($[61] !== LL) {
    t33 = LL.PRIVACY_POLICY();
    $[61] = LL;
    $[62] = t33;
  } else {
    t33 = $[62];
  }
  let t34;
  if ($[63] !== t33) {
    t34 = <NavItem title={t33} href="/privacy" />;
    $[63] = t33;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  let t35;
  if ($[65] !== LL || $[66] !== cookieConsent || $[67] !== handleOpenCookieSettings) {
    t35 = cookieConsent ? <NavItem title={LL.COOKIE_SETTINGS_LINK()} onClick={handleOpenCookieSettings} /> : null;
    $[65] = LL;
    $[66] = cookieConsent;
    $[67] = handleOpenCookieSettings;
    $[68] = t35;
  } else {
    t35 = $[68];
  }
  let t36;
  if ($[69] !== t24 || $[70] !== t32 || $[71] !== t34 || $[72] !== t35) {
    t36 = <List subheader={t24}>{t32}{t34}{t35}</List>;
    $[69] = t24;
    $[70] = t32;
    $[71] = t34;
    $[72] = t35;
    $[73] = t36;
  } else {
    t36 = $[73];
  }
  let t37;
  if ($[74] !== t21 || $[75] !== t36) {
    t37 = <Box role="presentation" sx={t9}>{t21}{t22}{t36}</Box>;
    $[74] = t21;
    $[75] = t36;
    $[76] = t37;
  } else {
    t37 = $[76];
  }
  let t38;
  if ($[77] !== isOpen || $[78] !== t3 || $[79] !== t37 || $[80] !== t4 || $[81] !== t8) {
    t38 = <SwipeableDrawer anchor="right" open={isOpen} onOpen={t3} onClose={t4} slotProps={t8}>{t37}</SwipeableDrawer>;
    $[77] = isOpen;
    $[78] = t3;
    $[79] = t37;
    $[80] = t4;
    $[81] = t8;
    $[82] = t38;
  } else {
    t38 = $[82];
  }
  return t38;
};
function _temp() {
  return signOut();
}
function _temp2(locale) {
  return <MenuItem key={locale} value={locale}><Typography>{localeLabels[locale]}</Typography></MenuItem>;
}